/**
 * ADR-0045 §6 (write side): turning "what the admin ticked" into the calls
 * that change one person's NON-PRODUCTION access.
 *
 * The Add user door answers "give this new person access"; this answers the
 * harder question the person's own record asks: they already have some
 * access — make it match what I just ticked. Comparing the two is fiddly
 * (roles change, accounts get revoked, guest access lapses), so the decision
 * lives here, DOM-free and tested, and the modal only renders it.
 *
 * Production is deliberately absent: production access is a
 * `_practitionerRole`, edited by the fields above this section, through a
 * different callable. This plans tenant-backed environments only.
 *
 * The verbs map to `training_invite_action`:
 *   invite    — mint or re-grant: also how a ROLE CHANGE is made, because
 *               invite() reuses an existing account, rewrites its grant and
 *               re-projects its claims (verified in training_invites.py).
 *   reinstate — re-enable a revoked account (invite does NOT re-enable).
 *   extend    — move a lapsed guest's expiry forward and re-enable.
 *   revoke    — disable. Never deletes: the standing rule.
 */
import type { AccessStatus, EnvironmentAccess } from "./user-catalogue";

/** What the admin ticked for one environment: a role, or null for unticked. */
export interface EnvironmentChoice {
  environment: string;
  role: string | null;
}

export type EnvironmentStepKind = "invite" | "reinstate" | "extend" | "revoke";

export interface EnvironmentStep {
  kind: EnvironmentStepKind;
  environment: string;
  /** Present on invite (the role being granted). */
  role?: string;
  /** Present on reinstate / extend / revoke (the account being acted on). */
  uid?: string;
  /** True when this step needs the "Access until" date. */
  needsExpiry?: boolean;
  /** One line describing what this step will do, for the confirm list. */
  describe: string;
}

/** The family role is the only one whose access lapses (ADR-0044). */
export const GUEST_ROLE = "family";

function statusOf(access: EnvironmentAccess | undefined): AccessStatus | "none" {
  return access ? access.status : "none";
}

/**
 * The steps that make `current` match `choices`, in the order they must run.
 *
 * Ordering matters within an environment: a revoked account is re-enabled
 * BEFORE its role is re-granted, so a failure leaves it disabled rather than
 * enabled with the wrong role.
 */
export function planEnvironmentChanges(
  current: EnvironmentAccess[],
  choices: EnvironmentChoice[],
): EnvironmentStep[] {
  const byEnv = new Map(current.map((a) => [a.environment, a]));
  const steps: EnvironmentStep[] = [];

  for (const { environment, role } of choices) {
    const access = byEnv.get(environment);
    const status = statusOf(access);

    if (role === null) {
      // Unticked. Only an account that can still be used needs disabling;
      // one already revoked or lapsed is left exactly as it is.
      if (status === "active") {
        steps.push({
          kind: "revoke", environment, uid: access!.uid,
          describe: `Revoke ${environment} access (disabled, never deleted)`,
        });
      }
      continue;
    }

    if (status === "none") {
      steps.push({
        kind: "invite", environment, role, needsExpiry: role === GUEST_ROLE,
        describe: `Give ${environment} access as ${role}`,
      });
      continue;
    }

    if (status === "revoked") {
      steps.push({
        kind: "reinstate", environment, uid: access!.uid,
        describe: `Restore their revoked ${environment} access`,
      });
    } else if (status === "expired") {
      steps.push({
        kind: "extend", environment, uid: access!.uid, needsExpiry: true,
        describe: `Extend their lapsed ${environment} access`,
      });
    }

    if (access!.role !== role) {
      steps.push({
        kind: "invite", environment, role, needsExpiry: role === GUEST_ROLE,
        describe: `Change their ${environment} role from ${access!.role} to ${role}`,
      });
    }
  }

  return steps;
}

/** True when any planned step needs the "Access until" date filled in. */
export function needsExpiry(steps: EnvironmentStep[]): boolean {
  return steps.some((s) => s.needsExpiry === true);
}

/** The choices a freshly opened modal starts from: every environment on
 *  offer, pre-ticked with the role the person holds there today. An account
 *  that is revoked or lapsed starts UNTICKED — it grants nothing now, so a
 *  ticked box would misdescribe the present. */
export function initialChoices(
  environments: string[],
  current: EnvironmentAccess[],
): EnvironmentChoice[] {
  const byEnv = new Map(current.map((a) => [a.environment, a]));
  return environments.map((environment) => {
    const access = byEnv.get(environment);
    return {
      environment,
      role: access && access.status === "active" ? access.role : null,
    };
  });
}
